import { useMemo, useState } from "react";
import { Loader2, ListChecks } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useReferenceIndex,
  useReferenceManufacturer,
  useApplyPlan,
} from "@/features/service/hooks";
import type { ReferenceServiceItem } from "@/types/service";

const SOURCE_LABELS: Record<string, string> = {
  official: "Factory",
  dealer: "Dealer/industry",
  community: "Community",
};

function intervalLabel(item: ReferenceServiceItem): string {
  const parts: string[] = [];
  if (item.interval_hours != null) parts.push(`${item.interval_hours} hrs`);
  if (item.interval_months != null) parts.push(`${item.interval_months} mo`);
  return parts.join(" or ");
}

interface ApplyPlanDialogProps {
  boatId: string;
  boatName: string;
  /** Pre-selected manufacturer key (engine field or make hint), "" when unknown. */
  suggestedManufacturer: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Wizard that instantiates service schedules for one boat from the
 * embedded manufacturer reference dataset. Items whose title already has an
 * active schedule on the boat are skipped server-side (re-runs are safe).
 */
export function ApplyPlanDialog({
  boatId,
  boatName,
  suggestedManufacturer,
  open,
  onOpenChange,
}: ApplyPlanDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {/* Mounting the body only while open gives it fresh state each time
            (the suggestion preselect) without effect-based syncing. */}
        {open && (
          <ApplyPlanBody
            boatId={boatId}
            boatName={boatName}
            suggestedManufacturer={suggestedManufacturer}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApplyPlanBody({
  boatId,
  boatName,
  suggestedManufacturer,
  onDone,
}: {
  boatId: string;
  boatName: string;
  suggestedManufacturer: string;
  onDone: () => void;
}) {
  const { data: reference } = useReferenceIndex();
  const [manufacturerKey, setManufacturerKey] = useState(suggestedManufacturer);
  const [includeBoatLevel, setIncludeBoatLevel] = useState(true);
  // null = "everything selected" (the default); an explicit set once the
  // user toggles anything. Reset to null when the manufacturer changes.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  const { data: mfr, isLoading } = useReferenceManufacturer(manufacturerKey, !!manufacturerKey);

  const allKeys = useMemo(() => new Set(mfr?.items.map((i) => i.key) ?? []), [mfr]);
  const selectedCount = allKeys.size - [...deselected].filter((k) => allKeys.has(k)).length;
  const allSelected = selectedCount === allKeys.size && allKeys.size > 0;

  const applyMutation = useApplyPlan();

  const changeManufacturer = (key: string) => {
    setManufacturerKey(key);
    setDeselected(new Set());
  };

  const toggleAll = () => {
    setDeselected(allSelected ? new Set(allKeys) : new Set());
  };

  const toggleItem = (key: string, checked: boolean) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSubmit = () => {
    if (!manufacturerKey) return;
    const itemKeys = mfr ? mfr.items.filter((i) => !deselected.has(i.key)).map((i) => i.key) : [];
    applyMutation.mutate(
      {
        boat_id: boatId,
        manufacturer_key: manufacturerKey,
        item_keys: itemKeys,
        include_boat_level: includeBoatLevel,
      },
      { onSuccess: () => onDone() }
    );
  };

  const manufacturerOptions =
    reference?.manufacturers.filter((m) => m.key !== "boat") ?? [];

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ListChecks className="h-5 w-5" /> Apply manufacturer plan
        </DialogTitle>
        <DialogDescription>
          Create recurring service schedules for {boatName} from the
          factory service intervals. Intervals follow "X hours or Y months,
          whichever comes first".
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-2">
        <div className="grid gap-2">
          <Label>Engine manufacturer</Label>
          <Select onValueChange={changeManufacturer} value={manufacturerKey || ""}>
            <SelectTrigger>
              <SelectValue placeholder="Choose manufacturer" />
            </SelectTrigger>
            <SelectContent>
              {manufacturerOptions.map((m) => (
                <SelectItem key={m.key} value={m.key}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {manufacturerKey && isLoading && <Skeleton className="h-32 w-full" />}

        {mfr && (
          <>
            <div className="flex items-center justify-between">
              <Label>Service items</Label>
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {allSelected ? "Clear all" : "Select all"}
              </Button>
            </div>
            <div className="flex flex-col gap-1.5 rounded-lg border p-3">
              {mfr.items.map((item) => (
                <label
                  key={item.key}
                  className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-accent"
                >
                  <Checkbox
                    checked={!deselected.has(item.key)}
                    onCheckedChange={(checked) => toggleItem(item.key, checked === true)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{item.title}</span>
                    <span className="block text-xs text-muted-foreground">
                      Every {intervalLabel(item)} · {SOURCE_LABELS[item.source_type] ?? item.source_type}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-accent">
              <Checkbox
                checked={includeBoatLevel}
                onCheckedChange={(checked) => setIncludeBoatLevel(checked === true)}
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">
                  Add boat-level items
                </span>
                <span className="block text-xs text-muted-foreground">
                  Anodes, bottom paint, winterization — apply to any boat.
                </span>
              </span>
            </label>
          </>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!manufacturerKey || selectedCount === 0 || applyMutation.isPending}
        >
          {applyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Apply {selectedCount > 0 ? `(${selectedCount})` : ""}
        </Button>
      </DialogFooter>
    </>
  );
}
